import { useEffect, useState, useRef } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface Stat {
  value: number;
  suffix: string;
  label: string;
}

const stats: Stat[] = [
  { value: 5000, suffix: "+", label: "Contacts extracted" },
  { value: 99, suffix: "%", label: "Field accuracy" },
  { value: 10, suffix: "×", label: "Faster than manual" },
];

const AnimatedCounter = ({
  value,
  suffix,
  isVisible,
}: {
  value: number;
  suffix: string;
  isVisible: boolean;
}) => {
  const [count, setCount] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    if (prefersReducedMotion) {
      setCount(value);
      hasAnimated.current = true;
      return;
    }
    hasAnimated.current = true;
    const duration = 1800;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, value, prefersReducedMotion]);

  return (
    <span className="tabular-nums">
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

export const StatsCounter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { ref: headerRef, animationClass: headerAnimation } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    threshold: 0.3,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-background">
      <div className="container px-4">
        <div ref={headerRef} className={`max-w-2xl mb-12 md:mb-16 ${headerAnimation}`}>
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
            By the numbers
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mt-3 mb-5">
            Hours saved. Errors avoided.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            Built for professionals who'd rather work than wrestle with a CRM.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border/80 border border-border rounded-xl overflow-hidden max-w-5xl">
          {stats.map((stat, i) => (
            <div key={i} className="bg-card p-6 md:p-10 lg:p-12">
              <div className="font-mono text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-primary leading-none">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} isVisible={isVisible} />
              </div>
              <div className="mt-4 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
